import {Component, Input, OnInit} from '@angular/core';
import {BaseModal} from '@ui-app/modals/base-modal';
import {EmployeeDataAccessService} from '../../core/services/data-access.service';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {FormControls} from '@shared-models/common.types';
import {IncorrectLengthValidator} from './tracking-id.validator';
import {TransferBadgeResult} from '@employee-models/common.models';

export type Controls = 'deliveryLocation' | 'trackingId' | 'filterQuery';

interface IDeliveryLocationOption {
    key: number;
    value: string;
}

@Component({
    selector: 'employee-transfer-badge-modal',
    templateUrl: './transfer-badge-modal.component.html',
    styleUrls: ['./transfer-badge-modal.component.less']
})
export class TransferBadgeModalComponent extends BaseModal implements OnInit {

    form: UntypedFormGroup;
    isSubmitted = false;

    @Input() popupTitle = 'Change delivery location';
    @Input() buttonsTitle = {
        dismiss: 'Cancel',
        confirm: 'Save'
    };
    @Input() currentDeliverySiteId: number;
    @Input() userId: string;

    readonly minQueryLength: number = 1;
    readonly notValidLocationNames: string[] = ['No name (US)', 'No name (USI)'];
    deliveryLocations: IDeliveryLocationOption[];
    pipeFilterQuery: string = '';
    isLocationDropdownOpen: boolean = false;
    filterBy: Record<string, string> = null;

    constructor(
        private employeeDataAccessService: EmployeeDataAccessService,
        private fb: UntypedFormBuilder
    ) {
        super();
    }

    ngOnInit(): void {
        this.getFilteredDeliveryLocations();

        this.form = this.fb.group({
            deliveryLocation: '',
            filterQuery: '',
            trackingId: ['', [Validators.required, IncorrectLengthValidator]]
        });
    }

    /**
     * @area Template.
     * @description Used to trim form control value.
     */
    trim(control: AbstractControl): void {
        control.setValue(control.value.trim());
    }

    getFilteredDeliveryLocations(): void {
        this.employeeDataAccessService.badgeRequestsService.getDeliveryLocationByEmployeeList(this.userId)
            .subscribe({
                next: deliveryLocations => {
                    this.deliveryLocations = deliveryLocations
                        .reduce((res, site) => {
                            if (!this.notValidLocationNames.includes(site.siteName) && site.siteId !== this.currentDeliverySiteId) {
                                res.push({ key: site.siteId, value: site.siteName });
                            }
                            return res;
                        }, []);
                }
            });
    }

    locationChanged(location: IDeliveryLocationOption): void {
        this.FC.deliveryLocation.setValue(location);
    }

    setFilterQuery(query: string): void {
        const trimmedQuery: string = query.trim();
        if (!trimmedQuery.length) {
            this.FC.filterQuery.setValue('');
            this.pipeFilterQuery = '';
        }

        this.pipeFilterQuery = (trimmedQuery.length < this.minQueryLength) ? '' : trimmedQuery;
        this.filterBy = this.pipeFilterQuery.length ? { value: this.pipeFilterQuery } : null;
    }

    toggle(): void {
        this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    }

    get hasFilteredResults(): boolean {
        return (!this.pipeFilterQuery.length)
            ? true
            : this.deliveryLocations
                .some(option => option.value.toLowerCase().includes(this.pipeFilterQuery.toLowerCase()));
    }

    submit(): void {
        this.isSubmitted = true;

        if (this.FC.trackingId.invalid) {
            return;
        }

        const replacedString = this.FC.trackingId.value.replace(/\s/g, '');

        if (replacedString !== this.FC.trackingId.value) {
            this.FC.trackingId.setValue(replacedString);
            return;
        }

        const actionResult: TransferBadgeResult = {
            newDeliveryLocation: this.FC.deliveryLocation.value.key,
            trackingId: this.FC.trackingId.value
        };

        this.action(actionResult);
    }

    get FC(): FormControls<Controls> {
        return this.form.controls as FormControls<Controls>;
    }

}
